import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { Button } from "../components/common/Button";
import { Calendar, Clock, MapPin, ArrowLeft, CheckCircle, Users, User, Gift } from "lucide-react";
import { Spinner } from "../components/common/Spinner";
import { ReservationPaymentNotice } from "../components/common/ReservationPaymentNotice";
import { KitOptionPicker } from "../components/common/KitOptionPicker";
import styles from "./WorkshopDetailPage.module.css";

interface WorkshopKit {
  _id: string;
  name: string;
  coverImage?: string;
}

interface WorkshopDetail {
  _id: string;
  name: string;
  description: string;
  date: string;
  time: string;
  endTime?: string;
  numberOfSeats: number;
  availableSeats: number;
  price: number;
  imageUrl: string;
  location: string;
  address?: string;
  level?: string;
  instructor?: string;
  availableKits?: WorkshopKit[];
  bundleName?: string;
  bundleDescription?: string;
  bundleImage?: string;
}

function getImageSrc(url: string) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${API_BASE_URL}${url}`;
}

export function WorkshopDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    numberOfSeats: 1,
    specialRequests: "",
    selectedKit: "",
    selectedFriendKit: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const successRef = useRef<HTMLDivElement>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [kitError, setKitError] = useState<string | null>(null);
  const [friendKitError, setFriendKitError] = useState<string | null>(null);

  useEffect(() => {
    const fetchWorkshopDetails = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/workshops/${id}`);
        if (!response.ok) {
          throw new Error("Failed to fetch workshop details");
        }
        const data = await response.json();
        console.log("Fetched workshop data:", data);
        setWorkshop(data.workshop);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching workshop details:", error);
        setError("Failed to load workshop details. Please try again.");
        setLoading(false);
      }
    };

    if (id) {
      fetchWorkshopDetails();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "beginner":
        return styles.levelBeginner;
      case "intermediate":
        return styles.levelIntermediate;
      case "advanced":
        return styles.levelAdvanced;
      default:
        return "";
    }
  };

  const handleBookNow = () => {
    if (workshop && workshop.availableSeats === 0) return;
    setBookingForm((prev) => ({ ...prev, selectedKit: "", selectedFriendKit: "" }));
    setKitError(null);
    setFriendKitError(null);
    setShowBookingModal(true);
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    setBookingForm({
      name: "",
      email: "",
      phone: "",
      numberOfSeats: 1,
      specialRequests: "",
      selectedKit: "",
      selectedFriendKit: "",
    });
    setBookingError(null);
    setBookingSuccess(false);
    setPhoneError(null);
    setKitError(null);
    setFriendKitError(null);
  };

  const validatePhoneNumber = (phone: string) => {
    if (!phone) {
      setPhoneError(null);
      return;
    }

    const digitsOnly = phone.replace(/\D/g, "");

    if (digitsOnly.length > 0 && !digitsOnly.startsWith("0")) {
      setPhoneError("Egyptian phone number must start with 0");
      return;
    }

    if (digitsOnly.length > 11) {
      setPhoneError("Phone number cannot exceed 11 digits");
      return;
    }

    if (digitsOnly.length > 0 && digitsOnly.length < 11) {
      setPhoneError(`${11 - digitsOnly.length} more digit${11 - digitsOnly.length === 1 ? "" : "s"} needed`);
      return;
    }

    setPhoneError(null);
  };

  const handleFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    // Real-time phone validation
    if (name === "phone") {
      validatePhoneNumber(value);
    }

    if (name === "numberOfSeats" && parseInt(value) === 1) {
      setFriendKitError(null);
      setBookingForm((prev) => ({
        ...prev,
        numberOfSeats: 1,
        selectedFriendKit: "",
      }));
      return;
    }

    setBookingForm((prev) => ({
      ...prev,
      [name]: name === "numberOfSeats" ? parseInt(value) : value,
    }));
  };

  const handleBookingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workshop) return;

    // Validate Egyptian phone number (must start with 0 and be 11 digits)
    const digitsOnly = bookingForm.phone.replace(/\D/g, "");
    if (!digitsOnly.startsWith("0") || digitsOnly.length !== 11) {
      setPhoneError(
        !digitsOnly.startsWith("0")
          ? "Egyptian phone number must start with 0"
          : "Egyptian phone number must be exactly 11 digits"
      );
      return;
    }

    if ((workshop.availableKits || []).length > 0 && !bookingForm.selectedKit) {
      setKitError("Please choose a kit for your workshop");
      return;
    }

    if (
      (workshop.availableKits || []).length > 0 &&
      bookingForm.numberOfSeats > 1 &&
      !bookingForm.selectedFriendKit
    ) {
      setFriendKitError("Please choose a kit for your friend");
      return;
    }

    setSubmitting(true);
    setBookingError(null);
    setPhoneError(null);
    setKitError(null);
    setFriendKitError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/reservations/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          workshop: workshop._id,
          guestName: bookingForm.name,
          guestEmail: bookingForm.email,
          guestPhone: bookingForm.phone,
          numberOfSeats: bookingForm.numberOfSeats,
          selectedKit: bookingForm.selectedKit || undefined,
          selectedFriendKit: bookingForm.selectedFriendKit || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create reservation");
      }

      setBookingSuccess(true);
      requestAnimationFrame(() => {
        successRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });

      // Refresh workshop to get updated available seats
      const workshopResponse = await fetch(
        `${API_BASE_URL}/api/workshops/${id}`,
      );
      const workshopData = await workshopResponse.json();
      setWorkshop(workshopData.workshop);
    } catch (error: any) {
      setBookingError(error.message || "Failed to book workshop");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <Spinner label="Loading workshop details..." size="lg" fullPage />
      </div>
    );
  }

  if (error || !workshop) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || "Workshop not found"}</p>
          <Button onClick={() => navigate("/products/workshops")} variant="primary">
            Back to Workshops
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      <div className={styles.container}>
        {/* Back Button */}
        <button onClick={() => navigate("/products/workshops")} className={styles.backButton}>
          <ArrowLeft size={20} />
          Back to All Workshops
        </button>

        {/* Main Content */}
        <div className={styles.content}>
          <div className={styles.imageSection}>
            <img
              src={workshop.imageUrl ? getImageSrc(workshop.imageUrl) : "/Workshop_Image.jpeg"}
              alt={workshop.name}
              className={styles.mainImage}
            />
          </div>
          <div className={styles.detailsSection}>
            <h1 className={styles.title}>{workshop.name}</h1>
            <p className={styles.description}>{workshop.description}</p>
            {workshop.bundleName && (
              <div className={styles.bundleSection}>
                {workshop.bundleImage && (
                  <img
                    src={getImageSrc(workshop.bundleImage)}
                    alt={workshop.bundleName}
                    className={styles.bundleImage}
                  />
                )}
                <div>
                  <h4 className={styles.bundleSectionTitle}>
                    <Gift size={18} />
                    {workshop.bundleName}
                  </h4>
                  {workshop.bundleDescription && (
                    <p className={styles.bundleSectionDesc}>
                      {workshop.bundleDescription}
                    </p>
                  )}
                </div>
              </div>
            )}
            {/* Workshop Info */}
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <Calendar className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Date</span>
                  <span className={styles.infoValue}>{formatDate(workshop.date)}</span>
                </div>
              </div>
              <div className={styles.infoItem}>
                <Clock className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Time</span>
                  <span className={styles.infoValue}>
                    {formatTime(workshop.time)}
                    {workshop.endTime ? ` - ${formatTime(workshop.endTime)}` : ""}
                  </span>
                </div>
              </div>
              <div className={styles.infoItem}>
                <MapPin className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Location</span>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workshop.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.infoValue} ${styles.locationLink}`}
                  >
                    {workshop.address || workshop.location}
                  </a>
                </div>
              </div>
              <div className={styles.infoItem}>
                <Users className={styles.infoIcon} size={20} />
                <div>
                  <span className={styles.infoLabel}>Availability</span>
                  <span className={styles.infoValue}>
                    {workshop.availableSeats > 0
                      ? `${workshop.availableSeats} spot${workshop.availableSeats === 1 ? "" : "s"} available`
                      : "Fully Booked"}
                  </span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className={styles.actions}>
              <Button
                variant="primary"
                onClick={handleBookNow}
                className={styles.bookButton}
                disabled={workshop.availableSeats === 0}
              >
                {workshop.availableSeats === 0 ? "Fully Booked" : "Book Now"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && workshop && (
        <div className={styles.modalOverlay} onClick={closeBookingModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeBookingModal}>
              ×
            </button>

            <h2 className={styles.modalTitle}>Book Workshop</h2>
            <h3 className={styles.modalWorkshopTitle}>{workshop.name}</h3>

            <div className={styles.modalDetails}>
              <p>
                <strong>Date:</strong> {formatDate(workshop.date)}
              </p>
              <p>
                <strong>Time:</strong> {formatTime(workshop.time)}
                {workshop.endTime ? ` - ${formatTime(workshop.endTime)}` : ""}
              </p>
              <p>
                <strong>Address:</strong>{" "}
                {workshop.address ? (
                  workshop.address
                ) : (
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(workshop.location)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.locationLink}
                  >
                    {workshop.location}
                  </a>
                )}
              </p>
              <p>
                <strong>Price:</strong> EGP {workshop.price}
              </p>
            </div>

            {bookingSuccess ? (
              <div className={styles.successMessage} ref={successRef}>
                <CheckCircle size={48} style={{ color: "#10b981" }} />
                <h3>Reservation Submitted!</h3>
                <p>
                  Your reservation request has been received and is pending approval.
                  You will receive a confirmation email once the business owner reviews
                  and confirms your booking.
                </p>
                <ReservationPaymentNotice />
              </div>
            ) : (
              <form
                className={styles.bookingForm}
                onSubmit={handleBookingSubmit}
              >
                {bookingError && (
                  <div className={styles.errorMessage}>{bookingError}</div>
                )}
                <div className={styles.formGroup}>
                  <label>Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={bookingForm.name}
                    onChange={handleFormChange}
                    placeholder="Enter your name"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={bookingForm.email}
                    onChange={handleFormChange}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={bookingForm.phone}
                    onChange={handleFormChange}
                    placeholder="01XXXXXXXXX (11 digits)"
                    required
                    style={{
                      borderColor: phoneError ? "#ef4444" : undefined,
                      borderWidth: phoneError ? "2px" : undefined,
                    }}
                  />
                  {phoneError && (
                    <span
                      style={{
                        color: "#ef4444",
                        fontSize: "0.875rem",
                        marginTop: "0.25rem",
                        display: "block",
                      }}
                    >
                      ⚠️ {phoneError}
                    </span>
                  )}
                </div>
                <div className={styles.formGroup}>
                  <label>Number of Spots</label>
                  <select
                    name="numberOfSeats"
                    value={bookingForm.numberOfSeats}
                    onChange={handleFormChange}
                    required
                  >
                    {[...Array(Math.min(workshop.availableSeats, 5))].map(
                      (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                {(workshop.availableKits || []).length > 0 && (
                  <div className={styles.formGroup}>
                    <label>Choose Your Kit</label>
                    <KitOptionPicker
                      kits={(workshop.availableKits || []).map((kit) => ({
                        id: kit._id,
                        name: kit.name,
                        image: kit.coverImage ? getImageSrc(kit.coverImage) : "",
                      }))}
                      value={bookingForm.selectedKit}
                      onChange={(kitId) => {
                        setKitError(null);
                        setBookingForm((prev) => ({
                          ...prev,
                          selectedKit: kitId,
                        }));
                      }}
                      hasError={!!kitError}
                    />
                    {kitError && (
                      <span
                        style={{
                          color: "#ef4444",
                          fontSize: "0.875rem",
                          marginTop: "0.25rem",
                          display: "block",
                        }}
                      >
                        ⚠️ {kitError}
                      </span>
                    )}
                  </div>
                )}
                {(workshop.availableKits || []).length > 0 &&
                  bookingForm.numberOfSeats > 1 && (
                    <div className={styles.formGroup}>
                      <label>Your Friend's Kit</label>
                      <KitOptionPicker
                        kits={(workshop.availableKits || []).map((kit) => ({
                          id: kit._id,
                          name: kit.name,
                          image: kit.coverImage ? getImageSrc(kit.coverImage) : "",
                        }))}
                        value={bookingForm.selectedFriendKit}
                        onChange={(kitId) => {
                          setFriendKitError(null);
                          setBookingForm((prev) => ({
                            ...prev,
                            selectedFriendKit: kitId,
                          }));
                        }}
                        hasError={!!friendKitError}
                      />
                      {friendKitError && (
                        <span
                          style={{
                            color: "#ef4444",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                            display: "block",
                          }}
                        >
                          ⚠️ {friendKitError}
                        </span>
                      )}
                      {bookingForm.numberOfSeats > 3 && (
                        <p className={styles.kitGroupNote}>
                          For groups larger than 3, we'll confirm the
                          remaining kit choices with you via WhatsApp when we
                          contact you.
                        </p>
                      )}
                    </div>
                  )}
                <div className={styles.formGroup}>
                  <label>Special Requests (Optional)</label>
                  <textarea
                    name="specialRequests"
                    value={bookingForm.specialRequests}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Any special requirements or questions?"
                  ></textarea>
                </div>
                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? "Booking..." : "Confirm Booking"}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
